/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { RefObject } from 'react'
import { toast } from 'react-toastify'

import { type Question } from '@/types'

export function generateId(): string {
  const S4 = function () {
    return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1)
  }
  return (
    S4() +
    S4() +
    '-' +
    S4() +
    '-' +
    S4() +
    '-' +
    S4() +
    '-' +
    S4() +
    S4() +
    S4()
  )
}

interface BuildQuestionTreeModel {
  id: string
  title: string
  value: string
  formIndex: number
  disabled?: boolean
  children?: Array<{
    id: string
    order: number
    disabled: boolean
    title: string
    value: string
  }>
}

export const buildQuestionTree = (
  questions: Question[],
  currentQuestion: any
) => {
  let filteredIndex = 0
  return questions
    .filter((item) => item.question_type.question_type !== 'datetime')
    .map((question) => {
      filteredIndex += 1
      const isMatrix = question.question_type.question_type === 'matrix'
      const currentIndex = filteredIndex

      if (isMatrix && question.question_rows?.length) {
        const isMatrixSelected = currentQuestion?.formula?.includes(
          `[Q${question.form_index}]`
        )
        const isAnyRowSelected = question.question_rows.some((row) =>
          currentQuestion?.formula?.includes(`[R${row.form_index}]`)
        )

        const children = question.question_rows.map((row, rowIndex) => {
          const currentRowIndex = rowIndex + 1
          const currentItemIndex = [currentIndex, currentRowIndex].join('.')

          return {
            id: row.id,
            order: row.order,
            disabled: isMatrixSelected,
            title: [currentItemIndex, row.text].join(' '),
            value: ['R', row.form_index].join(''),
          }
        })

        return {
          id: question.id,
          title: [currentIndex, question.text].join('. '),
          value: question.form_index,
          disabled: isMatrixSelected || isAnyRowSelected,
          formIndex: question.form_index,
          children,
        }
      }

      return {
        id: question.id,
        title: [currentIndex, question.text].join('. '),
        value: question.form_index,
        formIndex: question.form_index,
      }
    })
    .filter(Boolean) as unknown as BuildQuestionTreeModel[]
}

export const buildDateQuestionList = (questions: Question[]) => {
  let filteredIndex = 0
  return questions
    .map((question) => {
      const questionType = question.question_type.question_type

      if (!['datetime'].includes(questionType)) {
        return null
      }

      filteredIndex += 1
      const currentIndex = filteredIndex

      return {
        id: question.id,
        title: [currentIndex, question.text].join('. '),
        value: question.form_index,
        formIndex: question.form_index,
      }
    })
    .filter(Boolean) as unknown as Omit<BuildQuestionTreeModel, 'children'>[]
}

export const parseFormulaFromEditor = (
  editorRef: RefObject<HTMLDivElement | null>
) => {
  const editor = editorRef.current
  if (!editor) return

  let formula = ''

  for (const child of Array.from(editor.children)) {
    if (!(child instanceof HTMLElement)) continue

    const { type, formIndex, rowFormIndex, value } = child.dataset
    const content = child.textContent?.trim() || ''

    switch (type) {
      case 'number':
      case 'operator':
      case 'parentheses':
        formula += content
        break

      case 'field':
        if (rowFormIndex) {
          const cleanRowFormIndex = rowFormIndex.replace(/[^\d]/g, '')
          formula += `R${cleanRowFormIndex}`
        } else if (formIndex) {
          const cleanFormIndex = formIndex.replace(/[^\d]/g, '')
          formula += `Q${cleanFormIndex}`
        }
        break

      case 'function':
        formula += value
        break
    }
  }

  return formula
}

const parseNestedFunction = (htmlElement: HTMLElement) => {
  let formula = ''
  const elements = htmlElement.childNodes

  elements.forEach((node) => {
    if (
      node instanceof HTMLElement &&
      node.dataset &&
      node.dataset.type === 'addedField'
    ) {
      const value = node.dataset.value
      if (value) {
        if (value.startsWith('R')) {
          formula += `R${value.slice(1)}`
        } else {
          if (value.startsWith('Q')) {
            formula += `Q${value.slice(1)}`
          } else {
            formula += `Q${value}`
          }
        }
      }
    } else if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim()
      if (text && text !== '\u00A0') {
        formula += text
      }
    }
  })

  const removeSpaces = formula.replace(/\s+/g, '')
  return removeSpaces
}

export const parseFunctionFormulaFromEditor = (
  editorRef: RefObject<HTMLDivElement | null>
) => {
  const editor = editorRef.current

  if (!editor) return ''
  let formula = ''

  const parseNode = (node: Element) => {
    if (!(node instanceof HTMLElement)) return

    const { type } = node.dataset
    const content = node.textContent?.trim() || ''

    switch (type) {
      case 'function':
        formula += `${content}`
        break

      case 'parentheses':
        formula += content || ''
        break

      case 'addField': {
        const questionFormIndex = node.getAttribute('data-form-index')
        const questionId = node.getAttribute('data-question-id')
        const rowOrder = node.getAttribute('data-row-order') || '0'

        if (
          questionId !== null &&
          rowOrder !== null &&
          questionFormIndex === null
        ) {
          formula += `QR${questionId}_${rowOrder}`
        }
        if (questionFormIndex) {
          formula += `Q${questionFormIndex}`
        }
        break
      }

      case 'addDateField': {
        const questionFormIndex = node.getAttribute('data-form-index')
        if (questionFormIndex) {
          formula += `Q${questionFormIndex}`
        }
        break
      }

      case 'generic': {
        const genericValue = node.getAttribute('data-value')
        if (genericValue) {
          formula += genericValue
        }
        break
      }

      case 'rangeSeparator':
        formula += ':'
        break

      case 'semicolon':
        formula += ';'
        break

      case 'comma':
        formula += ','
        break

      case 'whenTrue':
      case 'whenFalse':
      case 'value':
      case 'condition':
        formula += parseNestedFunction(node)
        break

      default:
        break
    }

    for (const child of node.children) {
      parseNode(child)
    }
  }

  for (const child of editor.children) {
    parseNode(child)
  }
  return formula
}

export const isValidFunctionFormula = (formula: string): boolean => {
  const regex = /^([A-Z]+)\(([^)]+)\)$/i
  const match = formula.match(regex)

  if (!match) {
    toast.error('Invalid formula format')
    return false
  }

  const [, functionName, argument] = match

  if (!argument.trim().length) {
    toast.error('Empty value')
    return false
  }

  if (functionName.toLowerCase() === 'agedetail') {
    const qValueRegex = /^Q\d+/i
    if (!qValueRegex.test(argument.trim())) {
      toast.error('You need to select a date')
      return false
    }

    const agedetailRegex =
      /^Q\d+,\s*only_number=(true|false),\s*include_months=(true|false)$/i
    if (!agedetailRegex.test(argument.trim())) {
      toast.error('Invalid formula')
      return false
    }
  }

  if (!['if', 'countifs', 'agedetail'].includes(functionName.toLowerCase())) {
    toast.error('Invalid formula function')
    return false
  }

  return true
}
